import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Heart, Mail, User, MessageSquare } from 'lucide-react';

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!FORMSPREE_ENDPOINT) {
      setError('Form endpoint is not configured. Add REACT_APP_FORMSPREE_ENDPOINT to your .env file.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error || 'Could not send message.');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ backgroundColor: 'var(--bg-surface)' }}
      data-testid="contact-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-72 h-72 blob-pink float-anim opacity-25 pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-56 h-56 blob-pink float-anim-reverse opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          {/* Left: Info */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-2">
              <Heart size={14} style={{ color: 'var(--primary)' }} className="fill-current" />
              <p
                className="font-body text-xs font-semibold uppercase tracking-[0.2em]"
                style={{ color: 'var(--text-accent)' }}
              >
                Get In Touch
              </p>
            </div>
            <h2
              className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-tight"
              style={{ color: 'var(--text-primary)' }}
              data-testid="contact-heading"
            >
              Let's create
              <br />
              <span className="italic" style={{ color: 'var(--primary)' }}>something beautiful</span>
            </h2>
            <p
              className="font-body text-base md:text-lg leading-relaxed max-w-md"
              style={{ color: 'var(--text-secondary)' }}
            >
              Ready to turn your celebration into a stunning digital experience?
              Drop me a message and let's discuss your vision.
            </p>

            {/* Contact info cards */}
            <div className="space-y-4 pt-4">
              <motion.div
                className="flex items-center gap-4 p-4 rounded-xl bg-white/60 backdrop-blur-sm card-lift"
                whileHover={{ x: 5 }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-deep)' }}>
                  <Mail size={18} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wider" style={{ color: 'var(--text-accent)' }}>Email</p>
                  <p className="font-body text-sm font-medium" style={{ color: 'var(--text-primary)' }}>sitpu06@gmail.com</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center gap-4 p-4 rounded-xl bg-white/60 backdrop-blur-sm card-lift"
                whileHover={{ x: 5 }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-deep)' }}>
                  <MessageSquare size={18} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wider" style={{ color: 'var(--text-accent)' }}>Response Time</p>
                  <p className="font-body text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Within 24 hours</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {submitted ? (
              <motion.div
                className="flex flex-col items-center justify-center h-full space-y-4 py-12 bg-white/50 backdrop-blur-sm rounded-2xl p-8"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: 'backOut' }}
                data-testid="contact-success"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <CheckCircle size={56} style={{ color: 'var(--primary)' }} />
                </motion.div>
                <h3 className="font-heading text-2xl font-light" style={{ color: 'var(--text-primary)' }}>
                  Message Sent!
                </h3>
                <p className="font-body text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                  Thank you for reaching out. I'll get back to you soon.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="font-body text-sm cursor-pointer bg-transparent border-none"
                  style={{ color: 'var(--primary)' }}
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-6 bg-white/50 backdrop-blur-sm rounded-2xl p-8 md:p-10"
                data-testid="contact-form"
                style={{ boxShadow: '0 8px 40px rgba(212, 88, 122, 0.08)' }}
              >
                <div>
                  <label
                    htmlFor="name"
                    className="font-body text-xs font-semibold uppercase tracking-[0.15em] flex items-center gap-2 mb-3"
                    style={{ color: 'var(--text-accent)' }}
                  >
                    <User size={12} />
                    Your Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="input-minimal"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="font-body text-xs font-semibold uppercase tracking-[0.15em] flex items-center gap-2 mb-3"
                    style={{ color: 'var(--text-accent)' }}
                  >
                    <Mail size={12} />
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="input-minimal"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="font-body text-xs font-semibold uppercase tracking-[0.15em] flex items-center gap-2 mb-3"
                    style={{ color: 'var(--text-accent)' }}
                  >
                    <MessageSquare size={12} />
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your celebration..."
                    className="input-minimal resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {error && (
                  <p className="font-body text-sm text-red-500" data-testid="contact-error">
                    {error}
                  </p>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="btn-primary rounded-full w-full flex items-center justify-center gap-3 disabled:opacity-50"
                  data-testid="contact-submit-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="inline-block"
                    >
                      <Send size={16} />
                    </motion.span>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send size={16} />
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
